/**
 * 運用詳細 — 基本情報タブ
 *
 * 盤の基本情報、データ分析ナビゲーション、イベントログ、CSVエクスポートを表示する。
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Table,
  Space,
  Modal,
  DatePicker,
  Radio,
  message,
} from 'antd';
import type { Dayjs } from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import {
  HistoryOutlined,
  LineChartOutlined,
  DownloadOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import {
  exportTransmissionData,
  type PanelBasicInfo,
  type PanelStatusLogEntry,
} from '@/features/panels/services/panelApiService';
import { downloadBlob } from '@/shared/utils/downloadHelper';
import { logger } from '@/shared/lib/logger';
import { DateFactory } from '@/shared/utils/dateFactory';
import { FullText } from '@/shared/components/Display/FullText';
import { Typography } from 'antd';

const { Text } = Typography;

// ========== 定数 ==========

const BASIC_INFO_TAB_CONSTANTS = {
  LABEL_WIDTH: '150px',
  CARD_PADDING: '16px 24px',
  CARD_PADDING_MOBILE: '12px 16px',
  CARD_MIN_HEIGHT: '100px',
  PADDING_DIGITS: 2,
  TABLE_COLUMN_WIDTH: {
    TIME_DESKTOP: 180,
    TIME_MOBILE: 120,
    OPERATION_TYPE_DESKTOP: 120,
    OPERATION_TYPE_MOBILE: 80,
  },
  PAGINATION: {
    DESKTOP_PAGE_SIZE: 10,
    MOBILE_PAGE_SIZE: 5,
  },
  /** 状態履歴の詳細が空のときの表示（イベント名は常にあるため、通常は使われない） */
  EMPTY_STATUS_LOG_MESSAGE_PLACEHOLDER: '-',
} as const;

const EVENT_TYPE_CONFIG = {
  OPERATION_START: { color: 'green', text: '稼働開始' },
  OPERATION_END: { color: 'default', text: '稼働終了' },
  ERROR_OCCURRED: { color: 'red', text: 'エラー発生' },
  ERROR_RESOLVED: { color: 'blue', text: 'エラー解消' },
} as const;

// ========== Props ==========

interface BasicInfoTabProps {
  readonly panelBasicInfo: PanelBasicInfo;
  readonly statusLogs: readonly PanelStatusLogEntry[];
  readonly panelIdValue: number;
  readonly isMobile: boolean;
  readonly hasExportPanelDataPermission: boolean;
  readonly hasEditPanelPermission: boolean;
  readonly isSettingsDisabled: boolean;
  readonly onOpenSettings: () => void;
  readonly onNavigateToErrorTab: () => void;
}

// ========== コンポーネント ==========

export const BasicInfoTab: React.FC<BasicInfoTabProps> = ({
  panelBasicInfo,
  statusLogs,
  panelIdValue,
  isMobile,
  hasExportPanelDataPermission,
  hasEditPanelPermission,
  isSettingsDisabled,
  onNavigateToErrorTab,
  onOpenSettings,
}) => {
  const navigate = useNavigate();

  // CSVエクスポート関連state
  const [isExportModalVisible, setIsExportModalVisible] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportType, setExportType] = useState<'all' | 'period'>('all');
  const [exportStartDate, setExportStartDate] = useState<Dayjs | null>(null);
  const [exportEndDate, setExportEndDate] = useState<Dayjs | null>(null);

  // ========== ハンドラ ==========

  const handleNavigateToHistoryChart = (): void => {
    navigate(`/history-data?panelId=${panelIdValue}`);
  };

  const handleNavigateToRealtimeChart = (): void => {
    navigate(`/realtime-data?panelId=${panelIdValue}`);
  };

  const handleOpenExportModal = (): void => {
    setExportType('all');
    setExportStartDate(null);
    setExportEndDate(null);
    setIsExportModalVisible(true);
  };

  const handleCloseExportModal = (): void => {
    setIsExportModalVisible(false);
  };

  const disabledExportStartDate = (current: Dayjs): boolean => {
    if (exportEndDate === null) {
      return false;
    }
    return current.isAfter(exportEndDate, 'day');
  };

  const disabledExportEndDate = (current: Dayjs): boolean => {
    if (exportStartDate === null) {
      return false;
    }
    return current.isBefore(exportStartDate, 'day');
  };

  const handleExecuteExport = async (): Promise<void> => {
    if (exportType === 'period') {
      if (exportStartDate === null || exportEndDate === null) {
        message.warning('期間を指定してください');
        return;
      }
    }

    try {
      setIsExporting(true);

      const exportParams: {
        panelId: number;
        startDate?: string;
        endDate?: string;
      } = {
        panelId: panelIdValue,
      };

      if (exportType === 'period' && exportStartDate !== null && exportEndDate !== null) {
        exportParams.startDate = exportStartDate.format('YYYY-MM-DD');
        exportParams.endDate = exportEndDate.format('YYYY-MM-DD');
      }

      const result = await exportTransmissionData(exportParams);

      if (!result.success) {
        throw new Error('エクスポートに失敗しました');
      }

      if (result.message !== undefined) {
        message.info(result.message);
        setIsExportModalVisible(false);
        return;
      }

      if (result.blob !== undefined) {
        const timestamp = DateFactory.now().toISOString().slice(0, 10);
        const periodSuffix = exportType === 'period' && exportStartDate !== null && exportEndDate !== null
          ? `_${exportStartDate.format('YYYY-MM-DD')}_${exportEndDate.format('YYYY-MM-DD')}`
          : `_all_${timestamp}`;
        const filename = `transmission_panel${panelIdValue}_${panelBasicInfo.panelName}${periodSuffix}.csv`;
        downloadBlob({ blob: result.blob, filename });
        message.success('CSVエクスポートが完了しました');
        setIsExportModalVisible(false);
      }
    } catch (error) {
      logger.captureError(error, 'BasicInfoTab.handleExecuteExport');
      message.error('CSVエクスポートに失敗しました');
    } finally {
      setIsExporting(false);
    }
  };

  // ========== フォーマット ==========

  const formatTimestamp = (timestamp: Date): string => {
    const year = timestamp.getFullYear();
    const month = String(timestamp.getMonth() + 1).padStart(BASIC_INFO_TAB_CONSTANTS.PADDING_DIGITS, '0');
    const day = String(timestamp.getDate()).padStart(BASIC_INFO_TAB_CONSTANTS.PADDING_DIGITS, '0');
    const hour = String(timestamp.getHours()).padStart(BASIC_INFO_TAB_CONSTANTS.PADDING_DIGITS, '0');
    const minute = String(timestamp.getMinutes()).padStart(BASIC_INFO_TAB_CONSTANTS.PADDING_DIGITS, '0');
    const second = String(timestamp.getSeconds()).padStart(BASIC_INFO_TAB_CONSTANTS.PADDING_DIGITS, '0');
    return isMobile
      ? `${month}/${day} ${hour}:${minute}`
      : `${year}/${month}/${day} ${hour}:${minute}:${second}`;
  };

  const formatStatusLogMessage = (log: PanelStatusLogEntry): string => {
    if (log.errorDetails !== null && log.errorDetails.length > 0) {
      return `${log.eventTypeDisplay}: ${log.errorDetails.join(', ')}`;
    }
    return log.eventTypeDisplay;
  };

  // ========== テーブルカラム ==========

  const statusLogColumns: ColumnsType<PanelStatusLogEntry> = [
    {
      title: '時刻',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt: Date) => formatTimestamp(createdAt),
      width: isMobile
        ? BASIC_INFO_TAB_CONSTANTS.TABLE_COLUMN_WIDTH.TIME_MOBILE
        : BASIC_INFO_TAB_CONSTANTS.TABLE_COLUMN_WIDTH.TIME_DESKTOP,
    },
    {
      title: 'イベント',
      dataIndex: 'eventType',
      key: 'eventType',
      render: (eventType: keyof typeof EVENT_TYPE_CONFIG) => {
        const config = EVENT_TYPE_CONFIG[eventType];
        return <Tag color={config.color}>{config.text}</Tag>;
      },
      width: isMobile
        ? BASIC_INFO_TAB_CONSTANTS.TABLE_COLUMN_WIDTH.OPERATION_TYPE_MOBILE
        : BASIC_INFO_TAB_CONSTANTS.TABLE_COLUMN_WIDTH.OPERATION_TYPE_DESKTOP,
    },
    {
      title: '詳細',
      key: 'message',
      // スマホで省略するとエラー内容を読めないため、折り返して全文を表示する
      // dataIndex がない列では、antd は 1 つ目の引数に行のデータそのものを渡す
      render: (statusLogEntry: PanelStatusLogEntry) => (
        <FullText text={formatStatusLogMessage(statusLogEntry)} emptyPlaceholder={BASIC_INFO_TAB_CONSTANTS.EMPTY_STATUS_LOG_MESSAGE_PLACEHOLDER} />
      ),
    },
  ];

  return (
    <>
      <Card
        title="基本情報"
        className="mb-6"
        extra={
          hasEditPanelPermission ? (
            <Button
              type="text"
              icon={<SettingOutlined style={{ fontSize: '18px' }} />}
              onClick={onOpenSettings}
              title="設定"
              disabled={isSettingsDisabled}
            />
          ) : undefined
        }
        styles={{
          body: {
            padding: isMobile
              ? BASIC_INFO_TAB_CONSTANTS.CARD_PADDING_MOBILE
              : BASIC_INFO_TAB_CONSTANTS.CARD_PADDING,
            minHeight: BASIC_INFO_TAB_CONSTANTS.CARD_MIN_HEIGHT,
          },
        }}
      >
        <Descriptions column={1} size="middle" layout="horizontal">
          <Descriptions.Item label="デバイス識別番号" styles={{ label: { width: BASIC_INFO_TAB_CONSTANTS.LABEL_WIDTH } }}>
            <Text strong>{panelBasicInfo.deviceIdentificationDisplayText}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="盤名" styles={{ label: { width: BASIC_INFO_TAB_CONSTANTS.LABEL_WIDTH } }}>
            <Text strong>{panelBasicInfo.panelName}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="現場" styles={{ label: { width: BASIC_INFO_TAB_CONSTANTS.LABEL_WIDTH } }}>
            <Text strong>{panelBasicInfo.siteName}</Text>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="データ分析" className="mb-6">
        <Space
          direction={isMobile ? 'vertical' : 'horizontal'}
          className={isMobile ? 'w-full' : ''}
          wrap
        >
          <Button
            type="primary"
            icon={<HistoryOutlined />}
            onClick={handleNavigateToHistoryChart}
            className={isMobile ? 'w-full' : ''}
          >
            過去データグラフ
          </Button>
          <Button
            type="primary"
            icon={<LineChartOutlined />}
            onClick={handleNavigateToRealtimeChart}
            className={isMobile ? 'w-full' : ''}
          >
            リアルタイムデータグラフ
          </Button>
          {hasExportPanelDataPermission && (
            <Button
              icon={<DownloadOutlined />}
              onClick={handleOpenExportModal}
              className={isMobile ? 'w-full' : ''}
            >
              CSVエクスポート
            </Button>
          )}
        </Space>
      </Card>

      {hasExportPanelDataPermission && (
        <Modal
          title="CSVエクスポート設定"
          open={isExportModalVisible}
          onCancel={handleCloseExportModal}
          maskClosable={!isExporting}
          closable={!isExporting}
          footer={[
            <Button key="cancel" onClick={handleCloseExportModal} disabled={isExporting}>
              キャンセル
            </Button>,
            <Button
              key="export"
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExecuteExport}
              loading={isExporting}
            >
              エクスポート
            </Button>,
          ]}
          width={isMobile ? '90%' : 480}
        >
          <div className="py-4">
            <Radio.Group
              value={exportType}
              onChange={(e) => setExportType(e.target.value as 'all' | 'period')}
              className="mb-4"
            >
              <Space direction="vertical">
                <Radio value="all">全期間のデータをエクスポート</Radio>
                <Radio value="period">期間を指定してエクスポート</Radio>
              </Space>
            </Radio.Group>

            {exportType === 'period' && (
              <div className="mt-4">
                <Space direction="vertical" className="w-full" size="small">
                  <div>
                    <div className="mb-1">開始日</div>
                    <DatePicker
                      value={exportStartDate}
                      onChange={setExportStartDate}
                      format="YYYY-MM-DD"
                      placeholder="開始日"
                      className="w-full"
                      disabledDate={disabledExportStartDate}
                    />
                  </div>
                  <div>
                    <div className="mb-1">終了日</div>
                    <DatePicker
                      value={exportEndDate}
                      onChange={setExportEndDate}
                      format="YYYY-MM-DD"
                      placeholder="終了日"
                      className="w-full"
                      disabledDate={disabledExportEndDate}
                    />
                  </div>
                </Space>
              </div>
            )}
          </div>
        </Modal>
      )}

      <Card title="イベントログ">
        {statusLogs.length > 0 ? (
          <Table
            columns={statusLogColumns}
            dataSource={statusLogs as PanelStatusLogEntry[]}
            rowKey="logId"
            pagination={{
              pageSize: isMobile
                ? BASIC_INFO_TAB_CONSTANTS.PAGINATION.MOBILE_PAGE_SIZE
                : BASIC_INFO_TAB_CONSTANTS.PAGINATION.DESKTOP_PAGE_SIZE,
            }}
            size="small"
            scroll={isMobile ? { x: true } : undefined}
            // 従来はスマホのみ詳細列の ellipsis により fixed、PC は auto だった。省略をやめても列幅は変えない
            tableLayout={isMobile ? 'fixed' : 'auto'}
            onRow={(record) => ({
              onClick: record.eventType === 'ERROR_OCCURRED' || record.eventType === 'ERROR_RESOLVED'
                ? onNavigateToErrorTab
                : undefined,
              style: record.eventType === 'ERROR_OCCURRED' || record.eventType === 'ERROR_RESOLVED'
                ? { cursor: 'pointer' }
                : undefined,
            })}
          />
        ) : (
          <Text type="secondary">イベントログがありません</Text>
        )}
      </Card>
    </>
  );
};
